import { Filter, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useJobs, useQuotesList } from "@/api/hooks";
import { HelpPopover } from "@/components/HelpPopover";
import { StatusBadge } from "@/components/StatusBadge";
import { formatTime, shortId } from "@/lib/format";

const QUOTE_STATUSES = ["draft", "ready", "sent", "accepted", "declined", "expired"] as const;

/** All-quotes list for the contractor app. Mirrors the iOS
 *  ``QuotesListView`` so both surfaces have feature parity. Rows
 *  link to the existing ``/quotes/:id`` detail route.
 *
 *  Job titles + client names aren't on the ``QuoteResponse`` shape
 *  the API returns from ``GET /quotes``, so we fetch the active
 *  Jobs list in parallel and join by ``job_id`` client-side. Cheap
 *  query and React Query already caches both — moving the join to
 *  the server is a future polish if the page ever needs to scale
 *  past one org's worth of jobs.
 */
export function QuotesListPage() {
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const quotes = useQuotesList(statusFilter.size > 0 ? Array.from(statusFilter) : undefined);
  // We pull the active jobs to enrich each quote row with its job
  // title. Server-side `/quotes` doesn't include job_title yet; web
  // joins client-side until the API gains a denormalized field.
  const jobs = useJobs(undefined, "all");
  const jobIndex = useMemo(() => {
    const map = new Map<string, { title: string; client_name: string | null }>();
    for (const job of jobs.data ?? []) {
      map.set(job.id, {
        title: job.title,
        client_name: job.client_name ?? null,
      });
    }
    return map;
  }, [jobs.data]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = quotes.data ?? [];
    if (!needle) return all;
    return all.filter((q) => {
      const job = jobIndex.get(q.job_id);
      const haystack = [
        q.status,
        formatCents(q.total_cents),
        job?.title ?? "",
        job?.client_name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [quotes.data, jobIndex, search]);

  function toggleStatus(s: string) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-1.5">
          <h1 className="font-mono text-2xl font-bold tracking-tight">Quotes</h1>
          <HelpPopover title="What you're seeing">
            <p>
              Every quote across all your jobs. Filter by status above the table; tap a row to open
              the detail view + edit / send / track.
            </p>
            <p>
              Statuses: <strong>draft</strong> (still building), <strong>ready</strong> (priced +
              reviewed but unsent), <strong>sent</strong> (with the homeowner),{" "}
              <strong>accepted</strong> / <strong>declined</strong> / <strong>expired</strong>.
            </p>
          </HelpPopover>
        </div>
        <p className="mt-1 text-sm text-graphite-soft">
          Every quote across all your jobs. Filter by status, search by client / amount / job title.
        </p>
      </div>

      {/* Filter + search bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rule bg-paper-warm px-3 py-2">
        <Filter className="h-3.5 w-3.5 text-graphite-soft" aria-hidden />
        <span className="font-mono text-xs uppercase tracking-wider text-graphite-soft">
          Filter
        </span>
        <div className="flex flex-wrap gap-1.5">
          {QUOTE_STATUSES.map((s) => {
            const active = statusFilter.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={[
                  "rounded px-2 py-0.5 font-mono text-xs transition",
                  active
                    ? "bg-graphite text-paper"
                    : "bg-paper text-graphite-soft hover:bg-pencil-yellow-soft hover:text-graphite",
                ].join(" ")}
              >
                {s}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, amount, job…"
            className="w-64 rounded border border-rule bg-paper px-2 py-1 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-graphite-soft hover:text-graphite"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {quotes.isLoading && <p className="text-sm text-graphite-soft">Loading quotes…</p>}

      {quotes.error && !quotes.data && (
        <p className="text-sm text-red-pencil">
          {quotes.error instanceof ApiError ? quotes.error.detail : "Could not load quotes."}
        </p>
      )}

      {quotes.data && filtered.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <p className="text-graphite-soft">
            {search || statusFilter.size > 0
              ? "No quotes match those filters."
              : "No quotes yet. Create one from a job."}
          </p>
        </div>
      )}

      {quotes.data && filtered.length > 0 && (
        <>
          {/* Desktop: 7-column table (sm: and above). */}
          <div className="hidden overflow-hidden rounded-lg border border-rule bg-paper-warm sm:block">
            <table className="w-full">
              <thead className="border-b border-rule bg-paper">
                <tr>
                  <th className="label-mono px-4 py-2 text-left">Job</th>
                  <th className="label-mono px-4 py-2 text-left">Client</th>
                  <th className="label-mono px-4 py-2 text-left">Status</th>
                  <th className="label-mono px-4 py-2 text-right">Total</th>
                  <th className="label-mono px-4 py-2 text-right">Sent</th>
                  <th className="label-mono px-4 py-2 text-right">Updated</th>
                  <th className="label-mono px-4 py-2 text-right">ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => {
                  const job = jobIndex.get(q.job_id);
                  return (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/quotes/${q.id}`)}
                      onKeyDown={(e) => {
                        // Keyboard a11y — Enter / Space activate the
                        // row navigation (matches button semantics).
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/quotes/${q.id}`);
                        }
                      }}
                      // biome-ignore lint/a11y/useSemanticElements: <tr> is constrained to live inside <tbody>; a real <button> can't be the row container without breaking table semantics. Click + keyboard handlers + role=button + tabindex give activation parity.
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer border-b border-rule last:border-0 hover:bg-pencil-yellow-soft focus:bg-pencil-yellow-soft focus:outline-none"
                    >
                      <td className="px-4 py-3 font-medium text-graphite">{job?.title ?? "—"}</td>
                      <td className="px-4 py-3 text-graphite-soft">{job?.client_name ?? "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={q.status} />
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-graphite">
                        {formatCents(q.total_cents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-graphite-soft tabular-nums">
                        {q.sent_at ? formatTime(q.sent_at) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-graphite-soft tabular-nums">
                        {formatTime(q.updated_at)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[10px] text-graphite-faint">
                        {shortId(q.id)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked cards (below sm:). The table cramps badly
              on iPad portrait when the sidebar drawer is open, so we
              swap to a vertical card layout that keeps the same data
              visible without horizontal scroll. */}
          <div className="flex flex-col gap-2 sm:hidden">
            {filtered.map((q) => {
              const job = jobIndex.get(q.job_id);
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                  className="block w-full cursor-pointer rounded-lg border border-rule bg-paper-warm p-4 text-left hover:bg-pencil-yellow-soft focus:bg-pencil-yellow-soft focus:outline-none"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 font-medium text-graphite">{job?.title ?? "—"}</div>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="mt-1 text-sm text-graphite-soft">{job?.client_name ?? "—"}</div>
                  <div className="my-3 border-t border-rule" />
                  <div className="font-mono tabular-nums text-graphite">
                    {formatCents(q.total_cents)}
                  </div>
                  <div className="mt-1 font-mono text-xs text-graphite-soft tabular-nums">
                    Updated {formatTime(q.updated_at)}
                  </div>
                  {q.sent_at && (
                    <div className="mt-0.5 font-mono text-xs text-graphite-soft tabular-nums">
                      Sent {formatTime(q.sent_at)}
                    </div>
                  )}
                  <div className="mt-1 font-mono text-[10px] text-graphite-faint">
                    ID {shortId(q.id)}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(dollars);
}
